'use client';

import { Button, MediaGallery, Tooltip } from '@components/common';
import { DestructiveAction } from '@components/common/DestructiveAction/DestructiveAction';
import { TrashIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useSwrResumeInterviewVideoDelete } from '@root/src/hooks/useSwrApi/endpoints/resume/useSwrResumeInterviewVideoDelete';
import { useSwrJobInterviewDeleteInterviewVideo } from '@root/src/hooks/useSwrApi/endpoints/job-interview/useSwrJobInterviewDeleteInterviewVideo';
import { MediaGalleryHeaderActionsArgs } from '@root/src/types/media-gallery';
import VideoThumbnail from '@components/common/Video/Thumbnail';
import sendGoogleTagEvent from '@root/src/hooks/useSendGoogleTagEvent';

interface AiInterviewPreviewProps {
  locale: Locale;
  interviewVideos?: ApiSchema<'InterviewVideoRead'>[];
  requestUpdatedData?: () => void;
  hasDelete?: boolean;
}

/**
 * @description AiInterviewPreview component.
 *
 * @param {AiInterviewPreviewProps} props - The properties for the component.
 * - **locale**: Locale - The locale of the app.
 * - **interviewVideos**: ApiSchema<'InterviewVideoRead'>[] - An array of interview videos to display.
 * - **requestUpdatedData**: () => void - A callback function to request updated data.
 * - **hasDelete**: boolean - Whether the delete action is visible or not.
 */
export const AiInterviewPreview = (props: AiInterviewPreviewProps) => {
  const {
    locale, interviewVideos, requestUpdatedData, hasDelete,
  } = props;
  const { t } = useTranslation('common');
  const [localInterviewVideos, setLocalInterviewVideos] = useState(interviewVideos || []);
  const [showAllVideos, setShowAllVideos] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<ApiSchema<'InterviewVideoRead'> | null>(null);

  const handleCloseGallery = () => {
    requestUpdatedData?.();
  };

  const handleDeleteSuccess = (deletedVideoId: string) => {
    setLocalInterviewVideos((prevState) => {
      const newState = prevState.filter((video) => video.id !== deletedVideoId);
      if (newState.length === 0) {
        handleCloseGallery();
      }
      return newState;
    });
  };

  const { trigger: triggerDeleteCvBasedInterview } = useSwrResumeInterviewVideoDelete({
    locale,
    videoId: videoToDelete?.id ?? '',
    successCallback: (videoId) => {
      handleDeleteSuccess(videoId);
    },
  });

  const { trigger: triggerDeleteJobBasedInterview } = useSwrJobInterviewDeleteInterviewVideo({
    locale,
    videoId: videoToDelete?.id ?? '',
    jobSlug: videoToDelete?.jobSlug ?? '',
    successCallback: (videoId) => {
      handleDeleteSuccess(videoId);
    },
  });

  useEffect(() => {
    if (!videoToDelete) return;

    if (videoToDelete?.jobSlug) {
      void triggerDeleteJobBasedInterview();
    } else {
      void triggerDeleteCvBasedInterview();
    }
  }, [videoToDelete, triggerDeleteCvBasedInterview, triggerDeleteJobBasedInterview]);

  const renderHeaderActions = useCallback(({
    pauseGallery, currentItem,
  }: MediaGalleryHeaderActionsArgs) => {
    if (!hasDelete) return null;
    return (
      <>
        <Tooltip
          floating
          useFloatingPortal
          tooltipText={t('ai.interview.delete')}
          colorScheme="black"
          position="bottom"
          className="relative leading-none"
        >
          <DestructiveAction
            withConfirmation
            confirmationDescription={t('ai.interview.delete.confirmation')}
            styling="text"
            color="error"
            onClick={pauseGallery}
            onConfirm={async () => {
              setVideoToDelete(localInterviewVideos[currentItem]);
            }}
          >
            <TrashIcon className="size-6" />
          </DestructiveAction>
        </Tooltip>
      </>
    );
  }, [t, localInterviewVideos, hasDelete]);

  if (!localInterviewVideos?.length) return null;

  return (
    <MediaGallery
      items={localInterviewVideos.map((video) => ({ type: 'video', src: video.url, caption: video.question }))}
      withThumbnailNav
      headerActions={renderHeaderActions}
      onClose={handleCloseGallery}
      onSlideChange={() => { sendGoogleTagEvent('viewed_interview', 'Custom'); }}
    >
      {(openGalleryAtIndex) => (
        <>
          <div className="flex max-h-80 flex-col gap-2 overflow-y-auto">
            {(showAllVideos ? localInterviewVideos : localInterviewVideos.slice(0, 3)).map(
              (item, index) => (
                <div key={item.url} className="mb-3 flex items-center">
                  <h3 className="line-clamp-3 grow pr-2 text-sm">
                    {item.question}
                  </h3>
                  <Button
                    styling="none"
                    className={clsx(
                      'relative inline-flex size-20 h-14 min-w-20 shrink-0 items-center justify-center overflow-hidden rounded-md border-3 border-surface opacity-80 transition-opacity hover:opacity-100',
                    )}
                    onClick={() => {
                      openGalleryAtIndex(index);
                    }}
                  >
                    <VideoThumbnail src={item.url} shouldShowDuration />
                  </Button>
                </div>
              ),
            )}
          </div>
          {!showAllVideos && localInterviewVideos.length > 3 && (
            <div
              role="presentation"
              onClick={() => setShowAllVideos(true)}
              className="mx-auto mt-5 flex w-fit cursor-pointer items-center rounded-full bg-surface-100 px-4 py-2 text-center font-bold"
            >
              {`${t('global.label.see.all.interviews')} +${localInterviewVideos.length - 3}`}
            </div>
          )}
        </>
      )}
    </MediaGallery>
  );
};
